<template>
  <form
    v-if="step > 0"
    id="product-review-form"
    class="product-review-form | form"
    @submit.prevent="submitReview"
    ref="reviewForm"
    :method="method"
    :action="action"
    novalidate
  >
    <div
      class="product-review-form__step--1"
      v-if="step === 1"
    >
      <header class="product-review-form__header">
        <h3 class="product-review-form__headline">
          {{ $t('ads.reviews.form.headline') }}
        </h3>
        <div class="form__group--row">
          <div class="form__col-12">
            <ratings
              class="product-review-form__ratings"
              :class="{'product-review-form__ratings--vintages': initialModel.vintageOptions.length}"
              :ratings="[{value: 1}, {value: 2}, {value: 3}, {value: 4}, {value: 5}]"
              :rating="0"
              :required="true"
              :required-error="$t('ads.reviews.form.ratingError')"
              type="input"
              name="rating_stars"
              ref="rating"
              @change="onRatingChange"
            />
          </div>
        </div>

        <div
          v-if="initialModel.vintageOptions.length"
          class="form__group--row"
        >
          <div class="form__col-12">
            <select-vue
              class="product-review-form__vintage"
              name="vintage"
              v-model="model.vintage"
              :options="initialModel.vintageOptions"
              :initial-value="model.vintage"
            />
          </div>
        </div>
      </header>

      <section>
        <div class="form__group">
          <text-input
            class="product-review-form__input"
            v-model="model.author"
            name="author"
            type="text"
            :label="$t('ads.reviews.form.author')"
            :disabled="!isAuthorEditable"
            :required="!!validations.model.author.required"
            :error="getErrorText(v$.model.author.$errors)"
            @blur="v$.model.author.$touch()"
          />
          <p
            v-if="!isAuthorEditable && $t('ads.reviews.form.author.helptext')"
            class="product-review-form__input-hint | form__text"
            v-html="$t('ads.reviews.form.author.helptext', ['/account/username/change/'])"
          />
        </div>

        <div class="form__group">
          <text-input
            class="product-review-form__input"
            v-model="model.email"
            name="email"
            type="email"
            :label="$t('ads.reviews.form.email')"
            :required="!!validations.model.email.required"
            :disabled="isUserLoggedIn && !!initialModel.email"
            :error="getErrorText(v$.model.email.$errors)"
            @blur="v$.model.email.$touch()"
          />
          <p
            v-if="$t('ads.reviews.form.email.helptext')"
            class="product-review-form__input-hint | form__text"
            v-html="$t('ads.reviews.form.email.helptext')"
          />
        </div>

        <div class="form__group">
          <text-input
            class="product-review-form__input"
            v-model="model.title"
            name="title"
            type="text"
            :label="$t('ads.reviews.form.title')"
            :required="!!validations.model.title.required"
            :error="getErrorText(v$.model.title.$errors)"
            @blur="v$.model.title.$touch()"
          />
        </div>

        <div class="form__group">
          <text-input
            class="product-review-form__input roduct-review-form__textarea"
            v-model="model.message"
            name="message"
            type="textarea"
            :label="$t('ads.reviews.form.message')"
            :required="!!validations.model.message.required"
            :error="getErrorText(v$.model.message.$errors)"
            @blur="v$.model.message.$touch()"
          />
        </div>
      </section>

      <div
        v-if="initialModel.wine && settings.productReview.useRecommendationSection"
        class="product-review-form__section-toggle"
        :class="{
          'product-review-form__section-toggle--open': isRecoSectionOpen
        }"
        @click="isRecoSectionOpen = !isRecoSectionOpen"
      >
        {{ $t('ads.reviews.form.addMoreInfo') }}
      </div>

      <section
        v-if="initialModel.wine && isRecoSectionOpen"
        class="product-review-form__section"
      >
        <div class="form__group">
          <span class="product-review-form__vote-text | form__text">
            {{ $t('ads.reviews.form.recommend') }}
          </span>
          <vote-input
            class="product-review-form__vote"
            v-model="model.recommendation"
            name="recommendation"
          />
        </div>

        <options-list-input
          class="product-review-form__reco-options product-review-form__reco-options--food"
          :label="$t('ads.reviews.form.foodReco')"
          name="foodRecommendations"
          v-model="model.foodRecommendations"
          :multiple="true"
          :options="initialModel.foodOptions"
        >
          <template #default="list">
            <div
              class="winefinder__carousel__item"
              :class="{'winefinder__carousel__item--selected': list.selected}"
            >
              <span class="winefinder__carousel__item-icon">
                <span
                  class="winefinder__carousel__item-icon__icon"
                  :class="list.option.css"
                />
              </span>
              <span class="winefinder__carousel__item-name">
                {{ list.option.label }}
              </span>
            </div>
          </template>
        </options-list-input>

        <options-list-input
          class="product-review-form__reco-options product-review-form__reco-options--occasion"
          :label="$t('ads.reviews.form.occasionReco')"
          name="occasions"
          :multiple="true"
          v-model="model.occasions"
          :options="initialModel.occasionOptions"
        >
          <template #default="list">
            <div
              class="winefinder__carousel__item"
              :class="{'winefinder__carousel__item--selected': list.selected}"
            >
              <span class="winefinder__carousel__item-icon">
                <span
                  class="winefinder__carousel__item-icon__icon"
                  :class="list.option.css"
                />
              </span>
              <span class="winefinder__carousel__item-name">
                {{ list.option.label }}
              </span>
            </div>
          </template>
        </options-list-input>
      </section>

      <captcha-input
        v-if="captchaConfig"
        :captcha-site-key="captchaConfig.key"
        captcha-action="productReviewForm"
        ref="captchaInput"
        :error="getErrorText(v$.captchaToken.$errors)"
        @change="($event) => {captchaToken = $event}"
      />

      <div class="product-review-form__buttons">
        <p
          class="product-review-form__disclaimer | form__text"
        >
          <span v-html="$t('ads.reviews.form.moreInfo')" />
          <span
            v-if="$t('form.mandatory_notice')"
            class="form__mandatory-text-block"
          >
            {{ $t('form.mandatory_notice') }}
          </span>
        </p>

        <button
          type="submit"
          name="form-button"
          :disabled="loading"
          class="product-review-form__submit-button | button button--primary form__submit-button"
          :class="{
            'button--loading': loading
          }"
        >
          {{ $t('ads.reviews.form.submit') }}
        </button>
      </div>
    </div>

    <div
      class="product-review-form__step--2"
      v-if="step === 2"
    >
      <section class="product-review-form__thankyou">
        <h3
          class="product-review-form__headline product-review-form__headline--center"
          v-html="successHeadline"
        />
        <div
          class="product-review-form__ty-subline"
          v-html="successSubline"
        />
        <div
          class="product-review-form__ty-subline"
          v-if="showSetHint && $t('ads.reviews.form.thankYou.setProduct')"
          v-html="$t('ads.reviews.form.thankYou.setProduct', ['#setContains'])"
        />

        <template
          v-if="successState === 'CTACustomer'"
        >
          <div class="product-review-form__ty-subline">
            {{ $t('ads.reviews.form.yourReviews') }}
          </div>
          <a
            href="/account/reviews/"
            class="button button--primary"
          >
            {{ $t('ads.reviews.form.yourReviews.button') }}
          </a>
        </template>
        <template
          v-else-if="successState === 'CTAGuest'"
        >
          <div class="product-review-form__ty-subline">
            {{ $t('ads.reviews.form.login') }}
          </div>
          <a
            href="/account/reviews/"
            class="button button--primary"
          >
            {{ $t('ads.reviews.form.login.button') }}
          </a>

          <div class="product-review-form__ty-teaser">
            <div>
              {{ $t('ads.reviews.form.teaser.head') }}
            </div>
            <div v-html="$t('ads.reviews.form.teaser.content')" />
          </div>
        </template>
        <template
          v-else-if="successState === 'CTARegister'"
        >
          <div class="product-review-form__ty-subline">
            {{ $t('ads.reviews.form.register') }}
          </div>
          <a
            href="/registeraccount/"
            class="button button--primary"
          >
            {{ $t('ads.reviews.form.register.button') }}
          </a>
          <div class="product-review-form__ty-teaser">
            <div>
              {{ $t('ads.reviews.form.teaser.head') }}
            </div>
            <div v-html="$t('ads.reviews.form.teaser.content')" />
          </div>
        </template>
      </section>
    </div>
  </form>
</template>

<script lang="ts">
  import {defineComponent, PropType} from 'vue';
  import TextInput from '../input/TextInput.vue';
  import VoteInput from '../voteInput/voteInput.vue';
  import OptionsListInput from '../optionsListInput/optionsListInput.vue';
  import Ratings from '../ratings/ratings.vue';
  import SelectVue from '../select/select.vue';
  import CaptchaInput from '../captchaInput/captchaInput.vue';
  import FormMixin2 from '../../utilities/vueMixins/FormMixin/FormMixin2';
  import jsGlobalConfig from '../../globals/jsGlobalConfig';
  import * as scrollToElement from 'scroll-to-element';
  import type ReviewForm from '../../types/ReviewForm';
  import productStore from '../../stores/product/ProductStore';
  import {ProductDetailsSettings} from '../productDetails/productDetailsSettings';
  import diContainer from '../../utilities/js/diContainer/diContainer';
  import {intersectionService} from '../../utilities/js/intersectionService/intersectionService';
  import breakpoints from '../../variables/js/breakpoints';
  import {TrackingService} from '../../services/tracking/TrackingService';
  import pigeon from '../../utilities/js/pigeon/pigeon';

  export default defineComponent({
    name: 'ProductReviewForm',
    components: {
      TextInput,
      VoteInput,
      SelectVue,
      Ratings,
      OptionsListInput,
      CaptchaInput,
    },
    mixins: [FormMixin2],

    props: {
      initialModel: {
        type: Object as PropType<ReviewForm>,
        required: true,
      },
      activationHash: {
        type: String,
        required: true,
      },
      activationHide: {
        type: String,
        required: true,
      },
      initialRating: {
        type: Number,
        default: 4,
      },
      isUserLoggedIn: {
        type: Boolean,
        default: false,
      },
      isAuthorEditable: {
        type: Boolean,
        default: true,
      },
      showSetHint: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        model: {
          author: this.initialModel.author || '',
          email: this.initialModel.email || this.initialModel.entrypointEmail || '',
          title: '',
          message: '',
          vintage: this.initialModel.initialVintage || '',
          rating: parseInt(this.initialModel.rating) || this.initialRating,
          recommendation: '',
          foodRecommendations: [],
          occasions: []
        },

        productStore: productStore,
        settings: diContainer.get('productDetailsSettings') as ProductDetailsSettings,
        isRecoSectionOpen: false,
        step:  0,
        successState: '' as ''|'noCTA'|'CTACustomer'|'CTAGuest'|'CTARegister',
        successHeadline: null,
        successSubline: null,
        loading: false,
        captchaConfig: jsGlobalConfig().getCaptchaConfig('productReview'),
      }
    },

    mounted() {
      this.innerSuccessUrl = this.successUrl;
      this.innerAction = this.action;
      this.innerId = this.id;

      this.$nextTick(() => {
        this.onRatingChange(this.model.rating);
      });

      pigeon.subscribe('productReview:showForm', () => {
        this.activateReview();
      });

      pigeon.subscribe('productReview:seeReviews', () => {
        this.scrollToReviews();
      });
    },

    methods: {
      activateReview() {
        if (this.step === 0) {
          this.step = 1;
          this.model.vintage = this.initialModel.initialVintage || '';
          this.trackEvent('activation');

          const element = document.querySelector(this.activationHide);
          if (element) {
            element.classList.add('hide');
          }
        }

        this.scrollToReviews();
      },

      scrollToReviews() {
        setTimeout(() => {
          const scrollTop = window.innerWidth >= breakpoints.tablet ? this.settings.productReview.formScrollTop : 0;
          const tween = scrollToElement('.product-reviews', {duration: 400, offset: scrollTop});
          intersectionService.disable();
          tween.on('end', () => {
            setTimeout(() => {
              intersectionService.enable();
              intersectionService.activateAll('lazyload');
            }, 800);
          });
        }, 400);
      },

      onRatingChange(value) {
        this.model.rating = value || this.initialRating;
      },

      submitReview() {
        const ratingInput: any = this.$refs.rating;
        const isRatingValid = ratingInput.validate();
        if (!isRatingValid) {
          scrollToElement(ratingInput.$el, {
            offset: this.settings.productReview.submitScrollTop,
            duration: 300,
          });
          return;
        }

        this.loading = true;

        const formData = {
          productId: this.initialModel.productId,
          entrypointEmail: this.initialModel.entrypointEmail,
          sku: this.initialModel.sku,
          ...jsGlobalConfig().getCsrfTokenData(),
          ...this.model,
          captchaToken: this.captchaToken,
          foodRecommendations: this.model.foodRecommendations.join(','),
          occasions: this.model.occasions.join(','),
        };

        this.submit(this.serializeData(formData))
          .then((response) => {
            if (!this.v$.$error && isRatingValid) {
              scrollToElement(this.$el, {
                offset: this.settings.productReview.submitScrollTop,
                duration: 300,
              });
              this.step = 2;
              this.trackEvent('submission');

              const responseData = response.data.responseObject;
              this.successHeadline = responseData.reviewSuccess;
              this.successSubline = responseData.subline;
              if (responseData.accountReviewPageEnabled && this.isUserLoggedIn) {
                this.successState = 'CTACustomer';
              }
              else if (responseData.accountReviewPageEnabled && !this.isUserLoggedIn && responseData.knownUser) {
                this.successState = 'CTAGuest';
              }
              else if (responseData.accountReviewPageEnabled && responseData.knownUser === false) {
                this.successState = 'CTARegister';
              }
              else {
                this.successState = 'noCTA';
              }
            }
          })
          .catch(() => {
            this.loading = false;
          });
      },

      trackEvent(action: string) {
        TrackingService.trigger(TrackingService.EVENT_GENERIC, {
          event: 'productReview',
          action: action,
          itemId: this.productStore.productDetails.product.itemId,
        });
      }
    },

    computed: {
      validations() {
        return {
          model: {
            author: this.isAuthorEditable ? this.getValidationRules('ratingAuthor', this.isAuthorEditable, 'author') : {},
            email: !this.isUserLoggedIn ? this.getValidationRules('email', true, 'email') : {},
            title: this.getValidationRules('ratingTitle', false, 'title'),
            message: this.getValidationRules('longText', false, 'message'),
          },
          captchaToken: this.captchaValidationRule(),
        }
      }
    },

    validations() {
      return this.validations;
    }
  });
</script>
